import axios from 'axios';

/**
 * Reading an axios error safely.
 *
 * Across the repos this was written as `catch (error: any)` followed by
 * `error.response.status`. axios throws an error with **no `response` at all** for a
 * network failure, a timeout, a cancelled request or a CORS rejection — so that read threw
 * a `TypeError` inside the catch block and turned a handled failure into an unhandled one.
 * The user saw nothing instead of the message the branch was written to show.
 *
 * `any` is what allowed it: with `unknown`, the compiler asks for the check these provide.
 */

/**
 * The error envelope the backend sends. `message` is a string on most endpoints and an
 * object carrying a `code` on the teaching-path passing endpoints — this union is a record
 * of that inconsistency, not an endorsement of it.
 */
export interface ApiErrorBody {
  message?: string | { code?: string | number };
}

// Deeper than any wrapping here; a bound rather than a Set, since a chain can loop.
const MAX_CAUSES = 5;

/**
 * The axios error itself, or the one an API method wrapped as the `cause` of its own
 * (`new Error('copy assignment …', { cause })`, #241). Undefined when there is none.
 */
const requestErrorOf = (error: unknown) => {
  let current: unknown = error;
  for (let depth = 0; depth <= MAX_CAUSES && current !== undefined; depth += 1) {
    if (axios.isAxiosError(current)) {
      return current;
    }
    current = current instanceof Error ? current.cause : undefined;
  }
  return undefined;
};

/** The HTTP status, or undefined when the request never got a response. */
export const statusOf = (error: unknown): number | undefined =>
  requestErrorOf(error)?.response?.status;

/** The response body, or undefined when there was no response. */
export const bodyOf = (error: unknown): ApiErrorBody | undefined =>
  requestErrorOf(error)?.response?.data as ApiErrorBody | undefined;

/** The backend's `message` when it is a string; undefined when it is absent or an object. */
export const bodyMessageOf = (error: unknown): string | undefined => {
  const message = bodyOf(error)?.message;

  return typeof message === 'string' ? message : undefined;
};

/** The backend's `message.code` when `message` is the object form. */
export const bodyCodeOf = (error: unknown): string | number | undefined => {
  const message = bodyOf(error)?.message;

  return (message && typeof message === 'object') ? message.code : undefined;
};

/**
 * The thrown error's own message — axios' "Network Error" or "timeout of 0ms exceeded",
 * not the backend's. Falls back to String() so a thrown non-Error still reads.
 */
export const messageOf = (error: unknown): string =>
  (error instanceof Error ? error.message : String(error));
